import React, { useCallback, useEffect, useRef, useState } from 'react'
import Button from '../_library/Button'
import Card from '../_library/Card'
import ConfirmModal from '../_library/ConfirmModal'
import CustomToggle from '../_library/CustomToggle'
import { showAxiosError, showMessage } from '../utils/messenger'
import { isPasskeyRegistrationSupported, passkeyRequest, registerPasskey } from './passkey'
import {
  clearPhoneVerification,
  fetchTwoFactorStatus,
  generateTwoFactorSecret,
  saveAccountPhone,
  sendPhoneVerificationCode,
  setTwoFactorStatus,
  verifyAccountPhone,
} from './twoFactorApi'

const digitsOnly = value => String(value || '').replace(/\D/g, '')

const fallbackName = (passkey, passkeys) => {
  if (passkey.device_name) return passkey.device_name
  const number = passkeys.findIndex(item => item.id === passkey.id) + 1
  return `Passkey ${number}`
}

const formatDate = value => (value ? new Date(value).toLocaleDateString() : '—')

const MethodRow = ({
  iconClass,
  title,
  levelLabel,
  levelClass,
  checked,
  expanded,
  disabled,
  onToggle,
  children,
}) => (
  <div className="account-security-method">
    <div className="account-security-method-header">
      <i className={`fa ${iconClass} account-security-method-icon`} aria-hidden="true" />
      <div className="account-security-method-copy">
        <div className="account-security-method-title">{title}</div>
        <div className={`account-security-method-level ${levelClass}`}>{levelLabel}</div>
      </div>
      <CustomToggle
        id={`security-toggle-${title.replace(/\s+/g, '-').toLowerCase()}`}
        checked={checked}
        disabled={disabled}
        onChange={(_key, next) => onToggle(Boolean(next))}
      />
    </div>
    {(expanded ?? checked) && children ? (
      <div className="account-security-method-body">{children}</div>
    ) : null}
  </div>
)

/**
 * Sign-in protection list for the legacy dashboard: Passkey, Authenticator app,
 * Text message — same capability surface as the modern Account Settings card.
 */
const AccountSecurityMethods = () => {
  const requestVersionRef = useRef(0)
  const deleteInProgressRef = useRef(null)

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [busy, setBusy] = useState(false)

  const [passkeys, setPasskeys] = useState([])
  const [passkeysLoading, setPasskeysLoading] = useState(false)
  const [passkeysError, setPasskeysError] = useState(false)
  const [registering, setRegistering] = useState(false)
  const [deletingPasskeyId, setDeletingPasskeyId] = useState(null)

  const [modal, setModal] = useState(null)
  const [authStep, setAuthStep] = useState('secret')
  const [password, setPassword] = useState('')
  const [authCode, setAuthCode] = useState('')
  const [secretPayload, setSecretPayload] = useState(null)
  const [phone, setPhone] = useState('')
  const [phoneCode, setPhoneCode] = useState('')
  const [phoneCodeSent, setPhoneCodeSent] = useState(false)
  const [phoneDevCode, setPhoneDevCode] = useState(null)

  const passkeysSupported = isPasskeyRegistrationSupported()
  const authenticatorOn = status && Number(status.status) === 1
  const smsOn = Boolean(status && status.smsAvailable)
  const passkeyOn = passkeys.length > 0

  const closeModal = () => {
    setModal(null)
    setPassword('')
    setAuthCode('')
    setSecretPayload(null)
    setAuthStep('secret')
    setPhoneCode('')
    setPhoneCodeSent(false)
    setPhoneDevCode(null)
  }

  const loadStatus = useCallback(async (showFailure = true) => {
    setLoading(true)
    setLoadError(false)
    try {
      const next = await fetchTwoFactorStatus()
      setStatus(next)
      setPhone(next.phone || '')
    } catch (err) {
      if (showFailure) {
        setLoadError(true)
        showAxiosError(err, err.message || 'Could not load sign-in protection.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const loadPasskeys = useCallback(async (showFailure = true) => {
    if (!passkeysSupported) return
    const requestVersion = (requestVersionRef.current += 1)
    setPasskeysLoading(true)
    setPasskeysError(false)
    try {
      const response = await passkeyRequest('/passkey')
      if (requestVersion === requestVersionRef.current) {
        setPasskeys(response.data || [])
      }
    } catch (e) {
      if (showFailure && requestVersion === requestVersionRef.current) {
        setPasskeysError(true)
      }
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setPasskeysLoading(false)
      }
    }
  }, [passkeysSupported])

  useEffect(() => {
    loadStatus()
    if (passkeysSupported) loadPasskeys()
    return () => {
      requestVersionRef.current += 1
    }
  }, [loadStatus, loadPasskeys, passkeysSupported])

  useEffect(() => {
    if (!passkeysSupported || !window.desktop || !window.desktop.isDesktop) return undefined
    const handleFocus = () => loadPasskeys(false)
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [loadPasskeys, passkeysSupported])

  const handleGenerateSecret = async () => {
    if (!password) {
      showMessage('error', 'Enter your password to continue.', 5)
      return
    }
    setBusy(true)
    try {
      const secret = await generateTwoFactorSecret(password, true)
      setSecretPayload(secret)
      setAuthStep('confirm')
    } catch (err) {
      showAxiosError(err, err.message || 'Could not show authenticator setup.')
    } finally {
      setBusy(false)
    }
  }

  const handleEnable = async () => {
    if (!password) {
      showMessage('error', 'Enter your password to continue.', 5)
      return
    }
    if (!/^\d{6}$/.test(authCode)) {
      showMessage('error', 'Enter the 6-digit code from your authenticator app.', 5)
      return
    }
    setBusy(true)
    try {
      await setTwoFactorStatus({ password, status: 1, verificationCode: authCode })
      showMessage('success', 'Authenticator app is on.', 3)
      closeModal()
      await loadStatus(false)
    } catch (err) {
      showAxiosError(err, err.message || 'Could not turn on the authenticator app.')
    } finally {
      setBusy(false)
    }
  }

  const handleDisable = async () => {
    if (!password) {
      showMessage('error', 'Enter your password to continue.', 5)
      return
    }
    setBusy(true)
    try {
      await setTwoFactorStatus({ password, status: 0 })
      showMessage('success', 'Authenticator app is off.', 3)
      closeModal()
      await loadStatus(false)
    } catch (err) {
      showAxiosError(err, err.message || 'Could not turn off the authenticator app.')
    } finally {
      setBusy(false)
    }
  }

  const handleSavePhone = async () => {
    if (status && status.phoneVerified) return
    const number = digitsOnly(phone)
    if (number.length < 7 || number.length > 15) {
      showMessage('error', 'Enter a valid phone number with country code.', 5)
      return
    }
    setBusy(true)
    try {
      await saveAccountPhone(number)
      const sent = await sendPhoneVerificationCode()
      setPhoneCodeSent(true)
      setPhoneDevCode(sent.devCode || null)
      if (sent.devCode) {
        setPhoneCode(sent.devCode)
        showMessage('success', sent.message, 5)
      } else {
        showMessage('success', 'We texted you a code.', 3)
      }
      await loadStatus(false)
    } catch (err) {
      showAxiosError(err, err.message || 'Could not send a verification code.')
    } finally {
      setBusy(false)
    }
  }

  const handleVerifyPhone = async () => {
    if (!password) {
      showMessage('error', 'Enter your password to continue.', 5)
      return
    }
    if (!/^\d{6}$/.test(phoneCode)) {
      showMessage('error', 'Enter the 6-digit code from the text message.', 5)
      return
    }
    setBusy(true)
    try {
      await verifyAccountPhone(phoneCode, password)
      showMessage('success', 'Phone number verified.', 3)
      closeModal()
      await loadStatus(false)
    } catch (err) {
      showAxiosError(err, err.message || 'That phone code did not work.')
    } finally {
      setBusy(false)
    }
  }

  const handleRemoveSms = async () => {
    if (!password) {
      showMessage('error', 'Enter your password to continue.', 5)
      return
    }
    setBusy(true)
    try {
      await clearPhoneVerification(password)
      showMessage('success', 'Text backup removed.', 3)
      closeModal()
      await loadStatus(false)
    } catch (err) {
      showAxiosError(err, err.message || 'Could not remove the text message.')
    } finally {
      setBusy(false)
    }
  }

  const handleRegisterPasskey = async () => {
    if (deleteInProgressRef.current !== null) return
    setRegistering(true)
    try {
      if (window.desktop && window.desktop.openPasskeyEnrollment) {
        const response = await passkeyRequest('/passkey/desktop/enrollment-url')
        const enrollmentUrl = response.data && response.data.enrollment_url
        if (!enrollmentUrl) throw new Error('Could not start passkey setup.')
        await window.desktop.openPasskeyEnrollment(enrollmentUrl)
        return
      }
      await registerPasskey()
      showMessage('success', 'Passkey added.', 3)
      await loadPasskeys()
    } catch (caughtError) {
      if (!caughtError || caughtError.name !== 'NotAllowedError') {
        showAxiosError(caughtError, (caughtError && caughtError.message) || 'Could not add a passkey.')
      }
    } finally {
      setRegistering(false)
    }
  }

  const handleDeletePasskey = async passkey => {
    if (deleteInProgressRef.current !== null || registering) return
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this passkey? You will need a password or another passkey to sign in.')) {
      return
    }
    deleteInProgressRef.current = passkey.id
    setDeletingPasskeyId(passkey.id)
    try {
      requestVersionRef.current += 1
      setPasskeysLoading(false)
      await passkeyRequest(`/passkey/${passkey.id}`, 'delete')
      showMessage('success', 'Passkey removed.', 3)
      setPasskeys(current => current.filter(item => item.id !== passkey.id))
      await loadPasskeys(false)
    } catch (caughtError) {
      showAxiosError(caughtError, (caughtError && caughtError.message) || 'Could not remove that passkey.')
    } finally {
      deleteInProgressRef.current = null
      setDeletingPasskeyId(null)
    }
  }

  const modalTitle =
    modal === 'auth-enable' || modal === 'auth-disable'
      ? 'Authenticator app'
      : modal === 'sms' || modal === 'sms-disable'
        ? 'Text message'
        : ''

  const modalActions = (() => {
    if (modal === 'auth-enable' && authStep === 'secret') {
      return [
        { value: 'cancel', label: 'Cancel', className: 'btn btn-default', disabled: busy },
        { value: 'show', label: 'Show setup', className: 'btn btn-primary', loading: busy, disabled: busy },
      ]
    }
    if (modal === 'auth-enable' && authStep === 'confirm') {
      return [
        { value: 'cancel', label: 'Cancel', className: 'btn btn-default', disabled: busy },
        { value: 'enable', label: 'Turn on', className: 'btn btn-primary', loading: busy, disabled: busy },
      ]
    }
    if (modal === 'auth-disable') {
      return [
        { value: 'cancel', label: 'Cancel', className: 'btn btn-default', disabled: busy },
        { value: 'disable', label: 'Turn off', className: 'btn btn-danger', loading: busy, disabled: busy },
      ]
    }
    if (modal === 'sms' && !phoneCodeSent) {
      return [
        { value: 'cancel', label: 'Cancel', className: 'btn btn-default', disabled: busy },
        { value: 'send', label: 'Text me a code', className: 'btn btn-primary', loading: busy, disabled: busy },
      ]
    }
    if (modal === 'sms' && phoneCodeSent) {
      return [
        { value: 'cancel', label: 'Cancel', className: 'btn btn-default', disabled: busy },
        { value: 'resend', label: 'Resend code', className: 'btn btn-default', disabled: busy },
        { value: 'verify', label: 'Verify phone', className: 'btn btn-primary', loading: busy, disabled: busy },
      ]
    }
    if (modal === 'sms-disable') {
      return [
        { value: 'cancel', label: 'Cancel', className: 'btn btn-default', disabled: busy },
        {
          value: 'remove-sms',
          label: 'Remove text message',
          className: 'btn btn-danger',
          loading: busy,
          disabled: busy,
        },
      ]
    }
    return [{ value: 'cancel', label: 'Cancel', className: 'btn btn-default' }]
  })()

  const onModalAction = value => {
    if (value === 'cancel') {
      closeModal()
      return
    }
    if (value === 'show') void handleGenerateSecret()
    if (value === 'enable') void handleEnable()
    if (value === 'disable') void handleDisable()
    if (value === 'send' || value === 'resend') void handleSavePhone()
    if (value === 'verify') void handleVerifyPhone()
    if (value === 'remove-sms') void handleRemoveSms()
  }

  return (
    <Card icon="fa-shield" title="Sign-in protection">
      <style>{`
        .account-security-method { border-bottom: 1px solid #ececf1; padding: 14px 0; }
        .account-security-method:last-child { border-bottom: none; }
        .account-security-method-header { display: flex; align-items: center; gap: 12px; }
        .account-security-method-icon { width: 22px; text-align: center; color: inherit; font-size: 18px; }
        .account-security-method-copy { flex: 1; min-width: 0; }
        .account-security-method-title { font-weight: 600; font-size: 15px; color: inherit; }
        .account-security-method-level { margin-top: 2px; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; }
        .account-security-method-level.more { color: #1a7f37; }
        .account-security-method-level.less { color: #c0392b; }
        .account-security-method-body { padding: 12px 0 4px 34px; }
        .account-security-modal-field { margin-bottom: 12px; }
        .account-security-modal-field label { display: block; font-weight: 600; margin-bottom: 4px; }
        .account-security-modal-field input { width: 100%; }
      `}</style>

      <p>Protect your account using a secondary authentication method.</p>

      {loadError ? (
        <div>
          <div className="alert alert-danger">Could not load sign-in protection.</div>
          <Button type="button" className="btn btn-default btn-shadow" onClick={() => loadStatus()}>
            Try again
          </Button>
        </div>
      ) : null}

      {!loadError && loading && !status ? <p>Loading…</p> : null}

      {!loadError && status ? (
        <div>
          {passkeysSupported ? (
            <MethodRow
              iconClass="fa-fingerprint"
              title="Passkey"
              levelLabel="Most secure"
              levelClass="more"
              checked={passkeyOn}
              expanded={passkeyOn || passkeysError}
              disabled={busy || registering || deletingPasskeyId !== null || passkeysError}
              onToggle={next => {
                if (passkeysError || passkeyOn) return
                if (next) void handleRegisterPasskey()
              }}
            >
              {passkeysError ? (
                <div>
                  <div className="alert alert-danger">Could not load your passkeys.</div>
                  <Button type="button" className="btn btn-default btn-shadow" onClick={() => loadPasskeys()}>
                    Try again
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Added</th>
                          <th>Last used</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {passkeysLoading && passkeys.length === 0 ? (
                          <tr>
                            <td colSpan={4}>Loading…</td>
                          </tr>
                        ) : null}
                        {!passkeysLoading && passkeys.length === 0 ? (
                          <tr>
                            <td colSpan={4}>No passkeys yet.</td>
                          </tr>
                        ) : null}
                        {passkeys.map(passkey => (
                          <tr key={passkey.id}>
                            <td>{fallbackName(passkey, passkeys)}</td>
                            <td>{formatDate(passkey.created_at)}</td>
                            <td>{passkey.last_used_at ? formatDate(passkey.last_used_at) : 'Never'}</td>
                            <td className="text-right">
                              <Button
                                type="button"
                                className="btn btn-danger btn-shadow"
                                loading={deletingPasskeyId === passkey.id}
                                disabled={deletingPasskeyId !== null || registering}
                                onClick={() => handleDeletePasskey(passkey)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    type="button"
                    className="btn btn-default btn-shadow"
                    loading={registering}
                    disabled={deletingPasskeyId !== null}
                    onClick={() => void handleRegisterPasskey()}
                  >
                    Add a passkey
                  </Button>
                </div>
              )}
            </MethodRow>
          ) : null}

          <MethodRow
            iconClass="fa-mobile"
            title="Authenticator app"
            levelLabel="More secure"
            levelClass="more"
            checked={Boolean(authenticatorOn)}
            disabled={busy}
            onToggle={next => {
              setPassword('')
              setAuthCode('')
              setSecretPayload(null)
              setAuthStep('secret')
              setModal(next ? 'auth-enable' : 'auth-disable')
            }}
          >
            <p style={{ margin: 0 }}>Your authenticator app asks for a code after your password.</p>
          </MethodRow>

          <MethodRow
            iconClass="fa-comment"
            title="Text message (SMS)"
            levelLabel="Less secure"
            levelClass="less"
            checked={smsOn}
            disabled={busy}
            onToggle={next => {
              setPassword('')
              setPhone(status.phone || '')
              setPhoneCode('')
              setPhoneCodeSent(false)
              setPhoneDevCode(null)
              setModal(next ? 'sms' : 'sms-disable')
            }}
          >
            {status.phone ? (
              <p style={{ margin: 0 }}>
                <strong>{status.phone}</strong>
              </p>
            ) : null}
          </MethodRow>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={Boolean(modal)}
        header={modalTitle}
        onClose={closeModal}
        onAction={onModalAction}
        actions={modalActions}
        content={
          <div>
            {modal === 'auth-enable' && authStep === 'secret' ? (
              <div>
                <p>Enter your password so we can show your authenticator setup.</p>
                <div className="account-security-modal-field">
                  <label htmlFor="legacy-2fa-password">Password</label>
                  <input
                    id="legacy-2fa-password"
                    type="password"
                    className="form-control"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>
            ) : null}

            {modal === 'auth-enable' && authStep === 'confirm' && secretPayload ? (
              <div>
                <p style={{ textAlign: 'center' }}>Scan this code with your authenticator app.</p>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <img src={secretPayload.qr} alt="Authenticator QR code" width={180} height={180} />
                </div>
                <p style={{ textAlign: 'center', fontSize: 12, color: '#777' }}>Or enter this key:</p>
                <p style={{ textAlign: 'center' }}>
                  <code style={{ wordBreak: 'break-all' }}>{secretPayload.secret}</code>
                </p>
                <div className="account-security-modal-field">
                  <label htmlFor="legacy-2fa-code">Authenticator code</label>
                  <input
                    id="legacy-2fa-code"
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={authCode}
                    onChange={e => setAuthCode(digitsOnly(e.target.value).slice(0, 6))}
                    disabled={busy}
                  />
                </div>
              </div>
            ) : null}

            {modal === 'auth-disable' ? (
              <div>
                <p>Enter your password to turn off the authenticator app.</p>
                <div className="account-security-modal-field">
                  <label htmlFor="legacy-2fa-disable-password">Password</label>
                  <input
                    id="legacy-2fa-disable-password"
                    type="password"
                    className="form-control"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>
            ) : null}

            {modal === 'sms' ? (
              <div>
                <p>
                  Enter your password and a mobile number we can text if you lose access to your
                  authenticator app.
                </p>
                <div className="account-security-modal-field">
                  <label htmlFor="legacy-sms-enable-password">Password</label>
                  <input
                    id="legacy-sms-enable-password"
                    type="password"
                    className="form-control"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className="account-security-modal-field">
                  <label htmlFor="legacy-sms-phone">Mobile number</label>
                  <input
                    id="legacy-sms-phone"
                    type="tel"
                    className="form-control"
                    autoComplete="tel"
                    placeholder="e.g. 64277110262"
                    value={phone}
                    onChange={e => setPhone(digitsOnly(e.target.value))}
                    disabled={busy || Boolean(status && status.phoneVerified)}
                  />
                </div>
                {phoneCodeSent ? (
                  <div>
                    {phoneDevCode ? (
                      <div className="alert alert-info">Local test code: {phoneDevCode}</div>
                    ) : null}
                    <div className="account-security-modal-field">
                      <label htmlFor="legacy-sms-code">Text code</label>
                      <input
                        id="legacy-sms-code"
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={phoneCode}
                        onChange={e => setPhoneCode(digitsOnly(e.target.value).slice(0, 6))}
                        disabled={busy}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {modal === 'sms-disable' ? (
              <div>
                <p>Enter your password to remove the text message backup.</p>
                <div className="account-security-modal-field">
                  <label htmlFor="legacy-sms-disable-password">Password</label>
                  <input
                    id="legacy-sms-disable-password"
                    type="password"
                    className="form-control"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>
            ) : null}
          </div>
        }
      />
    </Card>
  )
}

export default AccountSecurityMethods
